import api from '@/core/api/client';

export interface Plan {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  price: number;
  capabilityKeys?: string[];
  currency: string;
  interval: 'month' | 'semester' | 'year';
  limits: Record<string, number>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  activeSubscriptionsCount?: number;
  /** ID do Product no Stripe (preenchido após a primeira sync). */
  stripeProductId?: string | null;
  /** Quantas linhas de plan_prices este plano tem. */
  pricesCount?: number;
  /** Quantas dessas linhas já foram materializadas como Stripe Price. */
  syncedPricesCount?: number;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
  assignableOnly?: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CreatePlanData {
  isActive?: boolean;
  licensing?: { tier: 'lite' | 'pro' | 'ultra'; capabilityKeys: string[]; isCustom?: boolean; isPublic?: boolean; packageId?: string; expectedPackageVersion?: number };
  name: string;
  slug: string;
  description?: string;
  price: number;
  currency?: string;
  interval?: 'month' | 'semester' | 'year';
  limits?: Record<string, number>;
}

export interface UpdatePlanData {
  name?: string;
  slug?: string;
  description?: string;
  price?: number;
  currency?: string;
  interval?: 'month' | 'semester' | 'year';
  limits?: Record<string, number>;
  isActive?: boolean;
}

export const plansService = {
  async list(params: PaginationParams = {}): Promise<PaginatedResponse<Plan>> {
    const response = await api.get('/plans', { params });
    if (response.data.success) {
      return {
        data: response.data.data,
        pagination: response.data.pagination,
      };
    }
    throw new Error(response.data.message || 'Erro ao listar planos');
  },

  async get(id: string): Promise<Plan> {
    const response = await api.get(`/plans/${id}`);
    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Erro ao buscar plano');
  },

  async create(data: CreatePlanData): Promise<Plan> {
    const response = await api.post('/plans', data);
    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Erro ao criar plano');
  },

  async update(id: string, data: UpdatePlanData): Promise<Plan> {
    const response = await api.put(`/plans/${id}`, data);
    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Erro ao atualizar plano');
  },

  async delete(id: string): Promise<void> {
    const response = await api.delete(`/plans/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Erro ao deletar plano');
    }
  },

  async toggleActive(id: string, isActive: boolean): Promise<Plan> {
    const response = await api.patch(`/plans/${id}/toggle-active`, { isActive });
    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Erro ao alterar status do plano');
  },

  async listPrices(planId: string): Promise<PlanPriceRow[]> {
    const response = await api.get(`/plans/${planId}/prices`);
    if (response.data?.success) return response.data.data as PlanPriceRow[];
    throw new Error(response.data?.message || 'Erro ao listar preços');
  },

  async upsertPrice(planId: string, body: UpsertPlanPriceData): Promise<PlanPriceRow> {
    const response = await api.put(`/plans/${planId}/prices`, body);
    if (response.data?.success) return response.data.data as PlanPriceRow;
    throw new Error(response.data?.message || 'Erro ao salvar preço');
  },

  async deletePrice(planId: string, priceId: string): Promise<void> {
    const response = await api.delete(`/plans/${planId}/prices/${priceId}`);
    if (!response.data?.success) {
      throw new Error(response.data?.message || 'Erro ao remover preço');
    }
  },

  /**
   * Enfileira uma sincronização manual deste plano com o Stripe. O backend apenas
   * insere um job UPSERT em `billing_sync_outbox` e responde 202; o worker
   * (`StripeCatalogSyncWorker`, polling a cada 5s) materializa Product + Prices
   * e atualiza `stripePriceId` nas linhas correspondentes.
   */
  async syncStripe(planId: string): Promise<void> {
    const response = await api.post(`/plans/${planId}/sync-stripe`);
    if (!response.data?.success) {
      throw new Error(response.data?.message || 'Erro ao sincronizar com Stripe');
    }
  },
};

/**
 * Linha de plan_prices retornada pelo backend. Uma linha por moeda, cada uma mapeia para
 * UMA Price no Stripe (Orya é source of truth; Stripe é espelho).
 */
export interface PlanPriceRow {
  id: string;
  planId: string;
  currency: string;
  unitAmount: number;
  stripePriceId: string | null;
  stripePricesHash: string | null;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
  /** SYNCED quando já existe stripePriceId; PENDING se ainda aguardando worker outbox. */
  syncStatus: 'SYNCED' | 'PENDING';
}

export interface UpsertPlanPriceData {
  expectedUnitAmount?: number;
  currency: string;
  unitAmount: number;
  isDefault?: boolean;
}
